<div>
  <div class="container-fluid">
    <div class="mb-4">
      <h1 class="h2 mb-1 fw-bold">DJI Flights</h1>
      <p class="text-muted mb-0">Flights received from DJI across all organizations, including the ones nobody can act on yet</p>
    </div>

    @if (errorMessage) {
      <div class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
        <span><i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorMessage }}</span>
        <button type="button" class="btn btn-sm btn-outline-danger" (click)="reload()">Try again</button>
      </div>
    }

    <!-- Status tabs, each with its count -->
    <ul class="nav nav-tabs mb-3">
      @for (t of tabs; track t.key) {
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="t.key === tab.key" (click)="selectTab(t)">
            {{ t.label }}
            <span class="badge ms-1" [ngClass]="t.key === 'attention' && count(t) > 0 ? 'bg-danger' : 'bg-secondary'">{{ count(t) }}</span>
          </button>
        </li>
      }
    </ul>

    <!-- Filters -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label small text-body-secondary" for="dji-org">Organization</label>
            <select id="dji-org" class="form-select" [(ngModel)]="organizationId" (change)="onFilterChange()">
              <option value="">All organizations</option>
              <option [value]="noOrganization">No organization</option>
              @for (org of organizations; track org.id) {
                <option [value]="org.id">{{ org.name }}</option>
              }
            </select>
          </div>

          <div class="col-md-3 position-relative">
            <label class="form-label small text-body-secondary" for="dji-drone">Drone</label>
            @if (droneId) {
              <div class="form-control d-flex justify-content-between align-items-center">
                <span class="text-truncate"><i class="bi bi-box-seam me-1"></i>{{ droneLabel || droneId }}</span>
                <button type="button" class="btn-close" aria-label="Clear drone filter" (click)="clearDrone()"></button>
              </div>
            } @else {
              <input
                id="dji-drone"
                type="search"
                class="form-control"
                placeholder="Search by serial number or name"
                autocomplete="off"
                [(ngModel)]="droneSearch"
                (ngModelChange)="onDroneSearchInput($event)"
              />
              @if (droneResults.length > 0) {
                <div class="list-group position-absolute w-100 shadow z-3">
                  @for (drone of droneResults; track drone.id) {
                    <button type="button" class="list-group-item list-group-item-action" (click)="chooseDrone(drone)">
                      <div class="fw-semibold">{{ drone.serialNumber }}</div>
                      <div class="small text-muted">{{ drone.name }} · {{ drone.source === 'adn' ? 'ADN inventory' : drone.organizationName }}</div>
                    </button>
                  }
                </div>
              } @else if (searchingDrones) {
                <div class="form-text">Searching…</div>
              }
            }
          </div>

          <div class="col-md-2">
            <label class="form-label small text-body-secondary" for="dji-from">From</label>
            <input id="dji-from" type="date" class="form-control" [(ngModel)]="from" (change)="onFilterChange()" />
          </div>

          <div class="col-md-2">
            <label class="form-label small text-body-secondary" for="dji-to">To</label>
            <input id="dji-to" type="date" class="form-control" [(ngModel)]="to" (change)="onFilterChange()" />
          </div>

          <div class="col-md-2">
            @if (hasFilters) {
              <button type="button" class="btn btn-outline-primary w-100" (click)="clearFilters()">
                <i class="bi bi-x-circle me-1"></i>Clear filters
              </button>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Flights -->
    <div class="card">
      @if (loading && !result) {
        <div class="card-body text-center py-5">
          <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
        </div>
      } @else if (result && result.items.length === 0) {
        <div class="card-body text-center text-muted py-5">
          <i class="bi bi-airplane fs-1 d-block mb-2"></i>
          No DJI flights match these filters.
        </div>
      } @else if (result) {
        <div class="table-responsive" [class.opacity-50]="loading">
          <table class="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Flight</th>
                <th>Organization</th>
                <th>Drone</th>
                <th class="text-end">Area</th>
                <th>Duration</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (row of result.items; track row.intakeId) {
                <tr role="button" (click)="open(row)">
                  <td>{{ formatInstant(row.flightStartedAt) }}</td>
                  <td>
                    @if (row.organizationName) {
                      {{ row.organizationName }}
                    } @else {
                      <span class="badge bg-warning text-dark">No organization</span>
                    }
                  </td>
                  <td>
                    @if (row.drone) {
                      <div>{{ row.drone.serialNumber }}</div>
                      <div class="small text-muted">{{ row.drone.name }}</div>
                    } @else {
                      <span class="text-muted">{{ row.device.factorySn || row.device.hardwareId || 'No serial sent' }}</span>
                    }
                  </td>
                  <td class="text-end">{{ row.workAreaAcres != null ? (row.workAreaAcres | number: '1.0-1') + ' ac' : '—' }}</td>
                  <td>{{ formatDuration(row.durationSeconds) || '—' }}</td>
                  <td>
                    <span class="badge" [ngClass]="statusBadgeClass(row.linkStatus)">{{ statusLabel(row.linkStatus) }}</span>
                    @if (rowHint(row); as hint) {
                      <div class="small text-muted">{{ hint }}</div>
                    }
                  </td>
                  <td class="text-end"><i class="bi bi-chevron-right text-muted"></i></td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (result.paging.totalPages > 1) {
          <div class="card-footer d-flex justify-content-between align-items-center">
            <span class="small text-muted">{{ result.paging.totalItems }} flights · page {{ page }} of {{ result.paging.totalPages }}</span>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="page === 1">
                <button type="button" class="page-link" (click)="goToPage(page - 1)">Previous</button>
              </li>
              @for (p of pages; track p) {
                <li class="page-item" [class.active]="p === page">
                  <button type="button" class="page-link" (click)="goToPage(p)">{{ p }}</button>
                </li>
              }
              <li class="page-item" [class.disabled]="page === result.paging.totalPages">
                <button type="button" class="page-link" (click)="goToPage(page + 1)">Next</button>
              </li>
            </ul>
          </div>
        } @else {
          <div class="card-footer small text-muted">{{ result.paging.totalItems }} flights</div>
        }
      }
    </div>
  </div>
</div>
